import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from '../context/GlobalContext';
import { SECTIONS, SECTIONS_BOTTOM, SUBS, canAccess } from './navConfig';
import { REPORTS } from '../pages/reports/reportsManifest';
import { Search } from '../design/Icons';

// ⌘K / Ctrl-K command palette.
//
// Mounts once, hidden. Captures the global shortcut, collects nav targets
// from navConfig.js, and surfaces them as a fuzzy-searchable overlay. Also
// shows recent routes (stored in localStorage) so commonly-used screens
// bubble to the top.

const RECENT_KEY = 'sa.cmdk.recent.v1';
const MAX_RECENT = 6;

const loadRecent = () => {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); }
  catch { return []; }
};

const pushRecent = (href) => {
  const list = loadRecent().filter(x => x !== href);
  list.unshift(href);
  localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, MAX_RECENT)));
};

// Simple subsequence fuzzy match: does `query` appear as a subsequence
// in `text`? Returns a score where earlier matches score higher.
const fuzzy = (text, query) => {
  if (!query) return 0;
  const t = text.toLowerCase(), q = query.toLowerCase();
  let ti = 0, qi = 0, score = 0, lastMatch = -1;
  while (ti < t.length && qi < q.length) {
    if (t[ti] === q[qi]) {
      score += lastMatch === ti - 1 ? 3 : 1;
      if (ti === 0 || t[ti - 1] === ' ') score += 2;
      lastMatch = ti;
      qi++;
    }
    ti++;
  }
  return qi === q.length ? score : -1;
};

const CommandPalette = () => {
  const nav = useNavigate();
  const { uinfo } = useContext(GlobalContext);
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);

  // Global shortcut.
  useEffect(() => {
    const onKey = (e) => {
      const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
      const cmdK = (isMac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (cmdK) {
        e.preventDefault();
        setOpen(o => !o);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQ('');
      setSelected(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  const items = useMemo(() => {
    const out = [];
    const status = uinfo?.Status;
    const all = [...SECTIONS, ...SECTIONS_BOTTOM];
    for (const s of all) {
      if (!canAccess(s.roles, status)) continue;
      out.push({ title: s.label, subtitle: 'Navigate', href: s.base, group: 'Navigate' });
      // Reports has no SUBS entry — its sub-pages come from the manifest so
      // ⌘K keeps finding individual reports (e.g. "Reports: Top customers").
      if (s.key === 'reports') {
        for (const r of REPORTS) {
          if (!canAccess(r.roles, status)) continue;
          out.push({
            title:    `${s.label}: ${r.label}`,
            subtitle: r.code ? `Navigate · ${r.code}` : 'Navigate',
            href:     `${s.base}/${r.slug}`,
            group:    'Navigate',
          });
        }
      }
      const subs = SUBS[s.key === 'cog' ? 'settings' : s.key];
      if (subs) {
        for (const sub of subs) {
          if (!canAccess(sub.roles, status)) continue;
          out.push({
            title:    `${s.label}: ${sub.label}`,
            subtitle: 'Navigate',
            href:     sub.slug ? `${s.base}/${sub.slug}` : s.base,
            group:    'Navigate',
          });
        }
      }
    }
    out.push(
      { title: 'Sign out', subtitle: 'Session', href: '__logout', group: 'System' },
      { title: 'Toggle theme',    subtitle: 'Preferences',    href: '__density',  group: 'System' },
    );
    return out;
  }, [uinfo]);

  const filtered = useMemo(() => {
    if (!q.trim()) {
      const recent = loadRecent();
      const recentItems = recent
        .map(href => items.find(i => i.href === href))
        .filter(Boolean)
        .map(i => ({ ...i, group: 'Recent' }));
      return [...recentItems, ...items.filter(i => !recent.includes(i.href))].slice(0, 14);
    }
    return items
      .map(i => ({ ...i, score: fuzzy(i.title, q) }))
      .filter(i => i.score >= 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 14);
  }, [items, q]);

  const go = (item) => {
    setOpen(false);
    if (item.href === '__logout') {
      window.dispatchEvent(new Event('sa:command:logout'));
      return;
    }
    if (item.href === '__density') {
      const cur = document.body.dataset.density || 'default';
      const next = cur === 'compact' ? 'default' : cur === 'default' ? 'roomy' : 'compact';
      document.body.dataset.density = next;
      localStorage.setItem('sa.density', next);
      return;
    }
    pushRecent(item.href);
    nav(item.href);
  };

  if (!open) return null;

  return (
    <div className="cmdk-overlay" onClick={() => setOpen(false)}>
      <div className="cmdk-card" onClick={e => e.stopPropagation()}>
        <div className="cmdk-input-row">
          <Search size={14}/>
          <input
            ref={inputRef}
            placeholder="Search screens, commands…"
            value={q}
            onChange={e => { setQ(e.target.value); setSelected(0); }}
            onKeyDown={e => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSelected(s => Math.min(filtered.length - 1, s + 1)); }
              if (e.key === 'ArrowUp')   { e.preventDefault(); setSelected(s => Math.max(0, s - 1)); }
              if (e.key === 'Enter')     { e.preventDefault(); const it = filtered[selected]; if (it) go(it); }
            }}
          />
          <kbd>esc</kbd>
        </div>
        <div className="cmdk-list">
          {filtered.length === 0 && (
            <div className="cmdk-empty label">No matches.</div>
          )}
          {filtered.map((it, i) => (
            <button
              key={`${it.href}-${i}`}
              type="button"
              className={`cmdk-row ${i === selected ? 'on' : ''}`}
              onClick={() => go(it)}
              onMouseEnter={() => setSelected(i)}
            >
              <span className="cmdk-title">{it.title}</span>
              <span className="cmdk-sub label">{it.group || it.subtitle}</span>
            </button>
          ))}
        </div>
        <div className="cmdk-foot label">
          <span>↑↓ to move</span>
          <span>↵ to select</span>
          <span>esc to close</span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
